import { Popover, Transition } from "@headlessui/react";
import {
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
} from "@heroicons/react/outline";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCart } from "react-use-cart";
import PriceLabel from "./PriceLabel";

const MiniCart = () => {
  const { t } = useTranslation();
  const { cartTotal, isEmpty, items, totalUniqueItems, updateItemQuantity } =
    useCart();

  let subtotal;

  if (items.every((item) => !item.price)) {
    subtotal = t("Price to be agreed");
  } else if (items.some((item) => !item.price)) {
    subtotal = `${t("From")} ${Number(cartTotal).toFixed(2)} €`;
  } else {
    subtotal = `${Number(cartTotal).toFixed(2)} €`;
  }

  return (
    <Popover className="flex relative">
      <Popover.Button
        aria-label={t("Cart")}
        className="inline-flex items-center relative"
      >
        <ShoppingCartIcon className="ml-2 w-5" aria-hidden="true" />
        {!isEmpty && (
          <span className="absolute bg-yellow flex font-medium h-5 items-center justify-center -right-2.5 rounded-full text-sm text-abbey -top-2.5 w-5">
            {totalUniqueItems}
          </span>
        )}
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute bg-white shadow-xl right-0 min-w-max mt-2 py-8 top-full w-96 z-10">
          {({ close }) => (
            <>
              <div className="mb-4 px-6">
                <div className="border-b-2 border-yellow font-bold pb-2 text-lg uppercase">
                  {t("Cart")}
                </div>
              </div>

              {isEmpty ? (
                <span className="font-medium px-6">{t("Cart is empty")}</span>
              ) : (
                <>
                  <div className="max-h-80 mb-4 overflow-y-auto px-6">
                    <table className="w-full">
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.id}>
                            <td className="font-medium py-2 w-1/2">
                              {item.CodiceWeb || item.Codice}
                            </td>
                            <td className="py-2 w-0">
                              <div className="border border-nobel flex">
                                <button
                                  onClick={() =>
                                    updateItemQuantity(
                                      item.id,
                                      Math.max(0, item.quantity - 1)
                                    )
                                  }
                                  className="border-r border-nobel px-2 py-1 disabled:bg-gray-100"
                                >
                                  <MinusIcon className="h-5 w-5" />
                                  <span className="hidden">{t("Reduce")}</span>
                                </button>
                                <input
                                  type="text"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    updateItemQuantity(item.id, value);
                                  }}
                                  className="px-2 py-1 text-center w-12"
                                />
                                <button
                                  onClick={() =>
                                    updateItemQuantity(
                                      item.id,
                                      item.quantity + 1
                                    )
                                  }
                                  className="border-l border-nobel px-2 py-1"
                                >
                                  <PlusIcon className="h-5 w-5" />
                                  <span className="hidden">{t("Add")}</span>
                                </button>
                              </div>
                            </td>
                            <td className="py-2 text-right w-1/2">
                              <PriceLabel product={item} showCurrency={true} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mb-6 px-6">
                    <div className="border-b-2 border-yellow font-bold mb-2 pb-2 uppercase">
                      <span className="whitespace-nowrap">{t("Subtotal")}</span>
                    </div>
                    <div className="text-right">{subtotal}</div>
                  </div>

                  <div className="px-6">
                    <Link className="button w-full" onClick={close} to="/cart">
                      {t("Go to cart")}
                    </Link>
                  </div>
                </>
              )}
            </>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

export default MiniCart;
